import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { HijriParts } from '@shared/lib/i18n/hijri';
import { usePreferences } from '@shared/model/preferences';
import { HijriDate, useHijriText } from '@shared/ui';
import { calendarApi, calendarKeys, type HijriDay } from '../api';

/** YYYY-MM-DD of an instant in a timezone. */
export function isoDay(at: Date | string | number, timeZone: string): string {
  const d = new Date(at);
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Hijri parts for the UI, or null. The date always comes from the API's authority table, never computed here. */
export function toHijriParts(day: HijriDay | undefined): HijriParts | null {
  if (!day?.hijri) return null;
  return { day: day.hijri.day, month: day.hijri.month, year: day.hijri.year, tentative: day.status === 'tentative' || Boolean(day.tentative) };
}

/** Map gregorian YYYY-MM-DD → Hijri parts (null when the authority has no entry for that day). */
export function useHijriDays(from: string, to: string, enabled = true) {
  const query = useQuery({
    queryKey: calendarKeys.hijri(from, to),
    queryFn: () => calendarApi.hijri(from, to),
    enabled: enabled && Boolean(from && to),
    staleTime: 60 * 60_000,
    retry: false,
  });
  const map = useMemo(() => new Map<string, HijriParts | null>((query.data?.data ?? []).map((d) => [d.gregorian, toHijriParts(d)])), [query.data]);
  return { map, query };
}

/** Header chip: today's Gregorian + Hijri date (date only). Shows nothing until a month start is entered. */
export function TodayHijri() {
  const timeZone = usePreferences((s) => s.timeZone);
  const today = isoDay(new Date(), timeZone);
  const { map } = useHijriDays(today, today);
  const hijri = map.get(today);
  if (!hijri) return null;
  return (
    <span className="hidden items-center gap-2 text-sm text-secondary lg:inline-flex">
      <HijriDate date={`${today}T12:00:00Z`} hijri={hijri} timeZone="UTC" />
    </span>
  );
}

/** Hijri line for a calendar day (renders nothing when the API has no date). */
export function DayHijri({ day, map }: { day: string; map: Map<string, HijriParts | null> }) {
  const text = useHijriText();
  const hijri = map.get(day);
  if (!hijri) return null;
  return (
    <span className="inline-flex items-center gap-2 text-xs text-muted">
      <bdi>{text(hijri)}</bdi>
    </span>
  );
}
